<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { APP_EVENTS, emitAppEvent } from '@/Utils/appEventBus'
import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import _ from 'lodash'
import { mediaPulseModel } from '@/components/Composable/mediaPulse'
import { isChatOnlyModerator } from '@/composables/useChatOnlyLock'
import StreamThumb from '@/components/ui/StreamThumb.vue'
import IconBase from '@/components/icon/IconBase.vue'
import ToggleStreamModal from '@/components/modals/ToggleStreamModal.vue'
import StreamService from '@/services/StreamService'
import Tooltip from '@/components/Atoms/Tooltip.vue'
import Badge from '../Badge.vue'
import IconCamera from '../icon/IconCamera.vue'
import IconCodec from '../icon/IconCodec.vue'
import IconSpeaker from '../icon/IconSpeaker.vue'
import IconBitrate from '../icon/IconBitrate.vue'
import IconLinkExternal from '../icon/IconLinkExternal.vue'
import IconTeam from '../icon/IconTeam.vue'
import IconBin from '../icon/IconBin.vue'
import IconMultistream from '../icon/IconMultistream.vue'
import IconMobile from '../icon/IconMobile.vue'
import FormButton from '../Atoms/FormButton.vue'
import DropDownMenu from '../Atoms/DropDownMenu.vue'
import DropDownMenuItem from '../Atoms/DropDownMenuItem.vue'
import IconElipsisVertical from '../icon/IconElipsisVertical.vue'
import IconArrowRight from '../icon/IconArrowRight.vue'
import StreamCardBadges from './StreamCard/StreamCardBadges.vue'

// Store, route, notification
const store = useStore()
const instance = getCurrentInstance()
const root = instance.proxy.$root
const router = useRouter()

// Emits
const emit = defineEmits({
	'delete-stream': () => true,
	'check-multistream': () => true,
	'toggle-stream': () => true
})

// Props
const props = defineProps({
	stream: {
		type: Object,
		required: true
	},
	shouldShowCompactModeStreams: {
		type: Boolean,
	},
	compactMode: {
		required: true
	},
	notAllowed: {
		type: Boolean,
		default() {
			return false
		},
	},
	hidePreview: {
		type: Boolean,
		default: false,
		required: false
	},
	previewMode: {
		type: Boolean,
		required: false
	},
	check: {
		type: Boolean,
		required: false
	},
	deleteBlock: {
		type: Boolean,
	},
	chosenStreams: {
		type: Array,
	},
	storage: {
		type: Boolean,
		required: false
	},
	transcoderView: {
		type: Boolean,
		default: false
	},
	newTabIcon: {
		type: Boolean,
		default: true
	},
	hasEnabledLinkedSources: {
		type: Boolean,
		default: false
	},
	tooltipLinkedSourceText: {
		type: String,
		default: ''
	},
	bandwidth: {
		type: Object,
	},
	hideActions: {
		type: Boolean,
		default: false
	},
	hideExternalLink: {
		type: Boolean,
		default: false
	},
	hideBadges: {
		type: Boolean,
		default: false
	},
})


// Media Pulse 
const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

// Refs
const scopeAlive = ref(true)
const streamStatus = ref(props.stream.enabled)
const notificationEnabled = ref(null)
const abr = ref(null)

// Computed
const userEmail = computed(() => store.getters['User/userEmail'])
const activeTeam = computed(() => store.getters['User/activeTeam'])
const memberPermission = computed(() => store.getters['User/userPermissions'])

const countryFlagIcon = computed(() => {
	let identifier = _.get(props.stream, ['uiRegion', 'identifier'], props.stream.region.identifier)
	// hack for studio@jackshoot.com
	if (props.stream.user === '5da088ba2564bd064957e474' && props.stream.uiRegion?._id === '62e2a0e108cb9dc2bcfb17d8') {
		identifier = 'kr'
	}
	// hack for kevin@stream.works
	if (props.stream.user === '5ba39c5e688503562c8097b4' && props.stream.uiRegion?._id === '63f3624d8ae252ca28ed79d2') {
		identifier = 'ae'
	}
	return `https://assets.castr.io/countryflags/${identifier}/flat/64.png`
})

const streamAlive = computed(() => !!(streamStatus.value && mediaPulse.value?.alive))

const checkDelPermission = computed(() => {
	const liveStreamDelete = props.stream.type === 'live' && memberPermission.value?.liveStreamDelete
	const reStreamDelete = props.stream.type === 'restream' && memberPermission.value?.recordedStreamDelete
	const preRecordedStreamDelete = props.stream.type === 'scheduled' && memberPermission.value?.preRecordedStreamDelete
	const subSecondStreams = props.stream.type === 'subsecond' && memberPermission.value?.subSecondStreamDelete
	const permission = liveStreamDelete || reStreamDelete || preRecordedStreamDelete || subSecondStreams
	return permission
})

const checkTogglePermission = computed(() => {
	const liveStreamToggle = props.stream.type === 'live' && memberPermission.value?.liveStreamToggle
	const reStreamToggle = props.stream.type === 'restream' && memberPermission.value?.recordedStreamToggle
	const preRecordedStreamToggle = props.stream.type === 'scheduled' && memberPermission.value.preRecordedStreamToggle
	const subSecondStreams = props.stream.type === 'subsecond' && memberPermission.value?.subSecondStreamManage
	const permission = liveStreamToggle || reStreamToggle || preRecordedStreamToggle || subSecondStreams
	return permission
})

const checkManagePermission = computed(() => {
	const liveStreamManage = props.stream.type === 'live' && memberPermission.value?.liveStreamManage
	const reStreamManage = props.stream.type === 'restream' && memberPermission.value?.recordedStreamManage
	const preRecordedStreamManage = props.stream.type === 'scheduled' && memberPermission.value?.preRecordedStreamManage
	const subSecondStreams = props.stream.type === 'subsecond' && memberPermission.value?.subSecondStreamManage
	const permission = liveStreamManage || reStreamManage || preRecordedStreamManage || subSecondStreams
	return permission
})

const canOpenManagePage = computed(() => {
	if (!activeTeam.value) return true
	if (checkManagePermission.value) return true
	return props.stream.type === 'live' && isChatOnlyModerator(memberPermission.value)
})

const getLink = computed(() => {
	if (props.transcoderView) {
		const routeObject = {
			name: 'TranscoderManage',
			params: { transcoderId: props.stream._id }
		}
		const resolvedRoute = router.resolve(routeObject)
		const link = resolvedRoute.href
		return link
	}

	if (canOpenManagePage.value) {

		let viewName = 'SimulcastManage'
		if (props.stream.type === 'live') {
			viewName = 'LivestreamManage'
		} else if (props.stream.type === 'ipcam') {
			viewName = 'CamstreamManage'
		} else if (props.stream.type === 'subsecond') {
			viewName = 'SubSecondManage'
		} else if (props.stream.type === 'scheduled') {
			viewName = 'ScheduledStreamManage'
		}

		if (!props.deleteBlock && !props.hidePreview && !props.previewMode) {
			const routeObject = {
				name: viewName,
				params: { streamId: props.stream._id }
			}
			const resolvedRoute = router.resolve(routeObject)
			const link = resolvedRoute.href
			return link
		}
	}
})

const shouldShowTooltipForDeleteButon = computed(() => {
	if (props.hasEnabledLinkedSources && props.tooltipLinkedSourceText) return true
	if (memberPermission.value && !checkDelPermission.value) return true
	return false
})

const contentForTooltipDeleteButon = computed(() => {
	if (props.hasEnabledLinkedSources && props.tooltipLinkedSourceText) return props.tooltipLinkedSourceText
	if (memberPermission.value && !checkDelPermission.value) return 'You don\'t have permission. Contact the team owner at ' + userEmail.value + ' to gain access.'
	return ''
})

// The permission this button needs, not the one next to it. Both of these used
// to ask `checkDelPermission`, so a member who could start and stop a stream but
// not delete one was told they had no permission — and then the stream toggled
// anyway, because the guard in `showStreamToggleModal` asks the right question.
const cannotToggleStream = computed(() => !!activeTeam.value && !checkTogglePermission.value)

const toggleStreamTooltipContent = computed(() => {
	if (cannotToggleStream.value) return 'You don\'t have permission. Contact the team owner at ' + userEmail.value + ' to gain access.'
	if (props.stream.enabled && !props.transcoderView && streamAlive) return 'Stop streaming, publishing'
	if (props.transcoderView && props.stream.enabled) return 'Stop transcoding'
	if (!props.stream.enabled && !props.transcoderView) return 'Enable your stream to start broadcasting'
	if (!props.stream.enabled && props.transcoderView) return 'Enable transcoder'
	return
})

const shouldShowRegion = computed(() => props.stream.type !== 'scheduled' && !props.transcoderView)

const shouldShowButtons = computed(() => {
	if (props.hidePreview || props.hideActions) return false
	return true
})

const shouldShowDropdown = computed(() => {
	if (props.compactMode && !props.hidePreview && !props.hideActions) return true
	return false
})

const countryText = computed(() => {
	return props.stream.user === '5da088ba2564bd064957e474' && props.stream.uiRegion?._id ===
		'62e2a0e108cb9dc2bcfb17d8'
		? 'Asia (Korea)'
		: (props.stream.user === '5ba39c5e688503562c8097b4' && props.stream.uiRegion?._id === '63f3624d8ae252ca28ed79d2')
			? 'Middle East (Dubai)'
			: props.stream.uiRegion
				? props.stream.uiRegion.name
				: props.stream.region.name === 'Sub Second Streaming - US'
					? 'North America'
					: props.stream.region.name
})

const streamCardWrapperClasses = computed(() => {
	if (props.compactMode) {
		return 'stream-card-compact w-full h-full relative rounded-lg group bg-surface-2 transition-colors duration-200 hover:bg-surface-3 px-3 py-2'
	}
	return 'flex flex-col w-full bg-surface-2 h-full relative rounded-xl group shadow-[0_1px_3px_rgba(0,0,0,0.15),0_10px_28px_-8px_rgba(0,0,0,0.4)] transition-[box-shadow,transform] duration-200 ease-out hover:shadow-[0_2px_6px_rgba(0,0,0,0.18),0_20px_44px_-10px_rgba(0,0,0,0.5)] hover:-translate-y-0.5'
})

const streamThumbClasses = computed(() => {
	const borderRadius = props.compactMode ? 'rounded' : 'rounded-t-xl'
	const size = props.compactMode ? 'h-12 w-24' : 'w-full aspect-video'
	const area = props.compactMode ? 'stream-card-compact__thumb' : ''
	return `${size} ${borderRadius} ${area}`.trim()
})

const streamCaptionClasses = computed(() => {
	if (props.compactMode) {
		return 'contents'
	}
	return 'flex flex-col gap-2 !p-4 flex-1 min-h-[132px]'
})
// Hooks
onMounted(async () => {
	try {
		subscribeMediaPulse(props.stream)
		if (props.stream.type) {
			// Use cached store to prevent fetching metadata for every stream card
			const store = instance.proxy.$store
			const streamMeta = await store.dispatch('StreamCache/getMetadata', { 
				streamId: props.stream._id,
				videoId: false 
			})
			abr.value = streamMeta.abr
		}
	} catch (error) {
		console.log(error)
	}
})

onUnmounted(() => {
	scopeAlive.value = false
	unsubscribeMediaPulse()
})


// Methods

const requestDelete = (ev) => {
	ev.preventDefault()
	ev.stopPropagation()
	if (!activeTeam.value || checkDelPermission.value) {
		emit('delete-stream', props.stream)
	}
}

const showStreamToggleModal = (ev) => {
	ev.preventDefault()
	ev.stopPropagation()
	if (props.notAllowed && !props.stream.enabled) {
		showModal('increase-limit')
	}
	if ((!props.notAllowed || (props.notAllowed && props.stream.enabled)) && (!activeTeam.value || checkTogglePermission.value)) {
		if (props.stream.type == 'restream') {
			emit('check-multistream', props.stream)
		} else {
			showModal('modalToggleStream' + props.stream._id, '#btnShow')
		}
	}
}

const navigateManage = (event, forseNavigate = false) => {
	// Check if click target is an interactive element (button, link, input, etc.)
	if (event) {
		const target = event.target;
		
		// Check if target or any parent is an interactive element
		// This catches buttons, links with href, inputs, and elements with role="button"
		const closestInteractive = target.closest('button, a[href], input, select, textarea, form, [role="button"], [role="link"]');
		
		// Also check if target itself is an interactive element
		const interactiveTags = ['button', 'a', 'input', 'select', 'textarea', 'form'];
		const isInteractiveTag = interactiveTags.includes(target.tagName.toLowerCase());
		
		// Check if target has an onclick handler (for legacy code)
		const hasOnClick = target.onclick !== null || target.getAttribute('onclick') !== null;
		
		// Check if target is inside an element with a click handler (Vue components typically add data attributes)
		// Vue components like FormButton, DropDownMenu will render as button elements, so closest('button') catches them
		// But we also check for common class patterns as a fallback
		const isInsideClickableComponent = target.closest('[class*="FormButton"], [class*="DropDown"], [class*="button"], [data-clickable]');
		
		if ((isInteractiveTag || closestInteractive || hasOnClick || isInsideClickableComponent) && !forseNavigate) {
			return; // Don't navigate if clicking on an interactive element
		}
	}

	if (props.transcoderView) {
		router.push({
			name: 'TranscoderManage',
			params: { transcoderId: props.stream._id }
		})
		return
	}

	if (canOpenManagePage.value) {
		let viewName = 'SimulcastManage'
		if (props.stream.type === 'live') {
			viewName = 'LivestreamManage'
		} else if (props.stream.type === 'ipcam') {
			viewName = 'CamstreamManage'
		} else if (props.stream.type === 'subsecond') {
			viewName = 'SubSecondManage'
		} else if (props.stream.type === 'scheduled') {
			viewName = 'ScheduledStreamManage'
		}
		if (!props.deleteBlock && !props.hidePreview && !props.previewMode) {
			router.push({
				name: viewName,
				params: { streamId: props.stream._id }
			})
		}
	}
}

const toggleStatus = async (ev, newStatus) => {
	emit('toggle-stream', props.stream)
}

const toggleDone = () => {
	emitAppEvent(APP_EVENTS.TOGGLE_FINISHED, true)
}

const allPlatforms = (platforms) => {
	return platforms?.length || 0
}
const linkedPlatforms = (platforms) => {
	return platforms?.filter(item => item.enabled).length || 0
}

const computedClassesForBadges = computed(() => {
	return props.hidePreview ? 'my-auto' : 'mt-auto'
})

</script>

<template>
	<figure
		:class="[streamCardWrapperClasses, compactMode && getLink ? 'cursor-pointer' : '']"
		@click="compactMode ? navigateManage($event) : undefined">
		<StreamCardBadges v-if="!compactMode" :abr="abr" :media-pulse="mediaPulse" :stream="stream"
			:compact-mode="compactMode" :hide-preview="hidePreview" :preview-mode="previewMode"
			:transcoder-view="transcoderView" :storage="storage" :bandwidth="bandwidth" />
		<div v-if="props.hidePreview === false"
			class="flex items-center justify-center bg-gradient-to-b from-surface-darker to-black relative shrink-0 overflow-hidden"
			@click="!compactMode ? navigateManage($event) : undefined"
			:class="[streamThumbClasses, getLink && !compactMode ? 'cursor-pointer' : '']">
			<stream-thumb :stream="stream" :media-pulse="mediaPulse" :compact-mode="compactMode"
				class="w-full h-full object-cover" />
			<!-- z-20: live preview video in StreamThumb uses z-10, so without this the
			     hover Manage overlay sits under the video and never shows. -->
			<div v-if="getLink && !compactMode"
				class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
				<span class="inline-flex items-center gap-1.5 text-sm font-medium text-white bg-surface-3/90 rounded-full px-3.5 py-1.5 shadow-lg">
					Manage
					<icon-base width="14" height="14" width-view-box="24" height-view-box="24" fill="none">
						<icon-arrow-right />
					</icon-base>
				</span>
			</div>
		</div>
		<figcaption class="relative" @click="!compactMode ? navigateManage($event) : undefined" :class="[streamCaptionClasses, getLink && !compactMode ? 'cursor-pointer' : '']">
			<div :class="compactMode ? 'min-w-0 max-w-full overflow-hidden stream-card-compact__title' : ''">
				<div class="flex flex-col min-w-0" :class="compactMode ? 'items-start' : ''">
					<div
						:class="compactMode ? 'inline-flex items-center min-w-0 max-w-full gap-1 overflow-hidden' : 'flex items-center min-w-0 gap-1'">
						<h3 :title="stream.name" class="truncate min-w-0 text-light-1"
							:class="[compactMode ? 'text-sm font-medium' : 'flex-1 text-lg']">
							<span class="truncate">{{ stream.name }}</span><Badge class="inline-flex" v-if="stream.added" title="Added" />
						</h3>
						<FormButton class="shrink-0" :class="{'hidden': hidePreview}" isIcon type="link" size="sm"
							v-if="newTabIcon && !hideExternalLink" target="_blank" :href="getLink">
							<icon-base>
								<IconLinkExternal />
							</icon-base>
						</FormButton>
					</div>
					<p v-if="shouldShowRegion" class="flex shrink-0 text-sm text-surface-8">
						<img width="20" :src="countryFlagIcon" class="mr-2">
						{{ countryText }}
					</p>
					<div v-if="transcoderView && stream.destination" class="flex gap-2 min-w-0 items-center">
						<icon-base class="shrink-0">
							<IconCamera />
						</icon-base>
						<div class="text-xs truncate min-w-0" :title="stream.destination?.name">{{ stream.destination?.name }}</div>
					</div>
					<div v-if="transcoderView && mediaPulse && mediaPulse.meta">
						<div class="flex items-center flex-wrap">
							<icon-base class="text-purple">
								<IconCodec />
							</icon-base>
							<span class="ml-2 text-s-m text-light text-500">{{ mediaPulse.meta.video.codec }}</span>
							<span class="mx-2">&#x2022;</span>
							<span class="text-s-m text-light text-500">{{ mediaPulse.meta.video.width }} x {{
								mediaPulse.meta.video.height }} ({{ mediaPulse.meta.video.bitrate }}kbps)</span>
						</div>
						<div v-if="mediaPulse.meta.audio" class="flex items-center gap-2">
							<icon-base class="text-success">
								<IconSpeaker />
							</icon-base>
							<span class="text-sm">{{ mediaPulse.meta.audio.codec }} {{
								mediaPulse.meta.audio.channels === 2 ? 'stereo' : '' }} ({{ mediaPulse.meta.audio.bitrate }}kbps)</span>
						</div>
						<div v-if="mediaPulse.meta.video" class="flex items-center gap-2">
							<icon-base class="text-pink">
								<IconBitrate />
							</icon-base>
							<span class="text-sm">{{ mediaPulse.meta.video.fps }} FPS</span>
						</div>
					</div>
				</div>
			</div>
			<DropDownMenu v-if="shouldShowDropdown" isIcon position="right" type="secondary" no-border
				class="shrink-0 stream-card-compact__kebab justify-self-end md:hidden">
				<template #toggle-button>
					<icon-base class="!ml-1">
						<IconElipsisVertical />
					</icon-base>
				</template>
				<template #menu-items>
					<DropDownMenuItem :disabled="cannotToggleStream" @click="showStreamToggleModal">
						{{ streamStatus ? 'Disable' : 'Enable' }}
					</DropDownMenuItem>
					<DropDownMenuItem :disabled="!!activeTeam && !checkDelPermission" @click="requestDelete($event)">
						Delete
					</DropDownMenuItem>
				</template>
			</DropDownMenu>
			<div v-if="compactMode" class="flex items-center gap-x-2 min-w-0 stream-card-compact__badges">
				<StreamCardBadges :abr="abr" :media-pulse="mediaPulse" :stream="stream" :compact-mode="compactMode"
					:hide-preview="hidePreview" :preview-mode="previewMode" :transcoder-view="transcoderView"
					:storage="storage" :bandwidth="bandwidth" />
				<template v-if="!hideBadges">
					<Tooltip class="h-6 flex" v-if="allPlatforms(stream.platforms) != 0">
						<Badge class="inline-flex items-center gap-x-1 h-6">
							<icon-base fill="transparent">
								<IconMultistream />
							</icon-base>
							{{ linkedPlatforms(stream.platforms) }} / {{ allPlatforms(stream.platforms) }}
						</Badge>
						<template #content>
							<span class="text-dark-8 text-small">
								{{ linkedPlatforms(stream.platforms) }} out of {{ allPlatforms(stream.platforms) }} platforms are enabled
							</span>
						</template>
					</Tooltip>
					<Tooltip class="h-6 flex" v-if="stream.sharedUsers?.length > 0">
						<Badge class="inline-flex items-center gap-x-1 h-6">
							<icon-base>
								<IconTeam />
							</icon-base>
							{{ stream.sharedUsers?.length }}
						</Badge>
						<template #content>
							<ul class="flex flex-col text-tiny">
								<li class="truncate" v-for="(user, index) in stream.sharedUsers" :key="index">{{ user }}</li>
							</ul>
						</template>
					</Tooltip>
				</template>
			</div>
			<div v-if="compactMode && !hideBadges" class="hidden md:flex items-center shrink-0 gap-x-2 stream-card-compact__actions">
				<Tooltip v-if="shouldShowButtons" class="group-hover:opacity-100 transition-opacity duration-200"
					:active="shouldShowTooltipForDeleteButon" :content="contentForTooltipDeleteButon">
					<FormButton type="danger" isIcon aria-label="Delete"
						:disabled="deleteBlock || (hasEnabledLinkedSources && tooltipLinkedSourceText)" size="sm"
						@click="requestDelete($event)">
						<icon-base>
							<IconBin />
						</icon-base>
					</FormButton>
				</Tooltip>
				<div v-if="!previewMode" class="flex items-center gap-x-2">
					<Tooltip v-if="(notificationEnabled || stream.notificationEnabled)" content="Phone alert enabled">
						<icon-base>
							<IconMobile />
						</icon-base>
					</Tooltip>
					<Tooltip v-if="shouldShowButtons" :active="streamAlive || cannotToggleStream"
						:content="toggleStreamTooltipContent">
						<FormButton :type="stream.enabled ? 'secondary' : 'main'" size="sm" :disabled="cannotToggleStream"
							:title="!transcoderView ? (streamAlive ? 'Stop streaming, publishing' : '') : 'Stop transcoding'"
							@click="showStreamToggleModal">
							{{ stream.enabled ? 'Disable' : 'Enable' }}
						</FormButton>
					</Tooltip>
				</div>
			</div>
			<div v-if="!compactMode && !hideBadges" class="flex items-center shrink-0 gap-x-2" :class="computedClassesForBadges">
				<Tooltip class="h-6 flex" v-if="allPlatforms(stream.platforms) != 0">
					<Badge class="inline-flex items-center gap-x-1 h-6">
						<icon-base fill="transparent">
							<IconMultistream />
						</icon-base>
						{{ linkedPlatforms(stream.platforms) }} / {{ allPlatforms(stream.platforms) }}
					</Badge>
					<template #content>
						<span class="text-dark-8 text-small">
							{{ linkedPlatforms(stream.platforms) }} out of {{ allPlatforms(stream.platforms) }} platforms are enabled
						</span>
					</template>
				</Tooltip>
				<Tooltip class="h-6 flex" v-if="stream.sharedUsers?.length > 0">
					<Badge class="inline-flex items-center gap-x-1 h-6">
						<icon-base>
							<IconTeam />
						</icon-base>
						{{ stream.sharedUsers?.length }}
					</Badge>
					<template #content>
						<ul class="flex flex-col text-tiny">
							<li class="truncate" v-for="(user, index) in stream.sharedUsers" :key="index">{{ user }}</li>
						</ul>
					</template>
				</Tooltip>
				<FormButton v-if="transcoderView" type="secondary" @click="navigateManage($event, true)" size="sm">
					Edit transcoder
				</FormButton>
				<Tooltip v-if="shouldShowButtons" class="ml-auto group-hover:opacity-100 transition-opacity duration-200 lg:opacity-0"
					:active="shouldShowTooltipForDeleteButon" :content="contentForTooltipDeleteButon">
					<FormButton type="danger" isIcon aria-label="Delete"
						:disabled="deleteBlock || (hasEnabledLinkedSources && tooltipLinkedSourceText)" size="sm"
						@click="requestDelete($event)">
						<icon-base>
							<IconBin />
						</icon-base>
					</FormButton>
				</Tooltip>
				<div v-if="!previewMode" class="flex justify-between items-center"
					:class="{ 'order-3 ml-0': transcoderView }">
					<Tooltip v-if="(notificationEnabled || stream.notificationEnabled)" content="Phone alert enabled">
						<icon-base>
							<IconMobile />
						</icon-base>
					</Tooltip>
					<Tooltip v-if="shouldShowButtons" :active="streamAlive || cannotToggleStream" :content="toggleStreamTooltipContent">
						<FormButton :type="stream.enabled ? 'secondary' : 'main'" size="sm" :disabled="cannotToggleStream"
							:title="!transcoderView ? (streamAlive ? 'Stop streaming, publishing' : '') : 'Stop transcoding'"
							@click="showStreamToggleModal">
							{{ stream.enabled ? 'Disable' : 'Enable' }}
						</FormButton>
					</Tooltip>
				</div>
			</div>
		</figcaption>
		<toggle-stream-modal :stream="stream" @modal-toggle="toggleStatus" @toggle-done="toggleDone"
			:transcoderView="transcoderView" />
	</figure>
</template>

<style scoped>
.stream-card-compact {
	display: grid;
	align-items: center;
	column-gap: 0.75rem;
	row-gap: 0.25rem;
	grid-template-columns: auto minmax(0, 1fr) auto;
	grid-template-areas:
		"thumb title kebab"
		"badges badges badges";
}

.stream-card-compact__thumb {
	grid-area: thumb;
}

.stream-card-compact__title {
	grid-area: title;
	min-width: 0;
	max-width: 100%;
	overflow: hidden;
}

.stream-card-compact__kebab {
	grid-area: kebab;
}

.stream-card-compact__badges {
	grid-area: badges;
}

.stream-card-compact__actions {
	grid-area: actions;
}

@media (min-width: 768px) {
	.stream-card-compact {
		grid-template-columns: auto minmax(0, 1fr) auto auto;
		grid-template-rows: auto;
		grid-template-areas: "thumb title badges actions";
	}
}
</style>
